import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { datiImpresaNellIscrizione } from "@/lib/iscrizione";
import { passwordDaImpostare } from "@/lib/accessoCodice";
import type { ReactNode } from "react";

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, emailVerified, profile, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Con la query: il link della mail «Vuole un tecnico» porta a
    // /banco-operatore?filo=…, e dopo l'accesso si deve tornare su QUEL filo.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (!emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  /**
   * Iscritto da Datacom (06/10/2026): e' entrato col codice arrivato per email
   * e non ha ancora una password sua. Prima di tutto la sceglie; finche' non
   * lo fa, ogni pagina protetta lo riporta li'.
   */
  if (passwordDaImpostare(user?.user_metadata) && location.pathname !== "/imposta-password") {
    return <Navigate to="/imposta-password" replace />;
  }

  /**
   * Senza partita IVA non si entra, e non importa da quale porta si e' passati.
   *
   * Chi si iscrive col modulo la scrive li'. Chi entra con Google o Apple non
   * compila niente: senza questo controllo si ritroverebbe dentro l'app con la
   * prova attiva, e nessuno gli avrebbe mai chiesto se e' un'azienda.
   *
   * Il confronto sul percorso evita il giro infinito: la pagina che chiede la
   * partita IVA non puo' pretenderla per essere aperta.
   *
   * Esente l'account di prova di un costruttore (25/09): e' Datacom a crearlo,
   * per un funzionario che non ha una partita IVA da installatore.
   */
  // Sull'iPhone (03/10) la partita IVA non si chiede: senza, si ha l'account
  // gratuito (il server da' solo norme, 3 a settimana). Vedi src/lib/iscrizione.ts.
  if (
    datiImpresaNellIscrizione() &&
    profile && !profile.partita_iva && !profile.account_prova && location.pathname !== "/completa-profilo" &&
    // la password prima: senza questa eccezione le due pagine si rimbalzerebbero
    location.pathname !== "/imposta-password"
  ) {
    return <Navigate to="/completa-profilo" replace />;
  }

  return <>{children}</>;
}